<template>
  <v-card class="fill-height marketing-resources" elevation="1">
    <!-- HEADER -->
    <v-card-title class="resources-header d-flex flex-wrap align-center py-3 px-5">
      <div class="d-flex align-center">
        <v-avatar color="primary lighten-5" size="36" class="me-3">
          <v-icon color="primary" size="20">mdi-folder-image</v-icon>
        </v-avatar>
        <span class="text-h6 font-weight-bold text--primary">Marketing Resources</span>
      </div>
      <v-spacer></v-spacer>

      <v-select
        v-model="selectedType"
        :items="typeOptions"
        item-text="text"
        item-value="value"
        label="Type"
        dense
        outlined
        hide-details
        rounded
        class="mr-3 mt-2 mt-sm-0 resource-filter"
        @change="fetchResources"
      >
        <template v-slot:prepend-inner>
          <v-icon size="18" color="grey">mdi-filter-variant</v-icon>
        </template>
      </v-select>

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search resources..."
        single-line
        hide-details
        dense
        outlined
        rounded
        class="mt-2 mt-sm-0 resource-search"
      ></v-text-field>
    </v-card-title>

    <v-divider></v-divider>

    <!-- EMPTY STATE (NO TYPE) -->
    <div v-if="!selectedType && !loading" class="d-flex flex-column align-center justify-center py-12 empty-state">
      <v-icon size="64" color="grey lighten-2">mdi-filter-outline</v-icon>
      <p class="mt-4 text-subtitle-1 grey--text text--darken-1 font-weight-medium">Select a resource type to begin</p>
    </div>

    <!-- LOADING STATE -->
    <div v-if="loading" class="d-flex flex-column align-center justify-center py-12">
      <v-progress-circular indeterminate color="primary" size="48" width="4" />
      <p class="mt-4 text-body-2 grey--text">Loading resources...</p>
    </div>

    <!-- RESOURCES GRID -->
    <v-container v-else-if="selectedType && filteredResources.length" fluid class="pa-5">
      <v-row dense>
        <v-col
          v-for="resource in filteredResources"
          :key="resource.id"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-card
            outlined
            hover
            class="resource-card d-flex flex-column fill-height"
            @mouseenter="resource.hover = true"
            @mouseleave="resource.hover = false"
          >
            <!-- PREVIEW -->
            <div class="resource-preview-wrapper">
              <v-img
                v-if="resource.type && resource.type.toLowerCase() === 'image'"
                :src="resource.file_url"
                height="140"
                cover
                class="resource-image"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-2" size="32" />
                  </v-row>
                </template>
              </v-img>

              <div v-else class="file-icon-container">
                <v-avatar size="56" :color="getFileColor(resource.filename)" class="mb-2">
                  <v-icon size="28" color="white">{{ getFileIcon(resource.filename) }}</v-icon>
                </v-avatar>
                <span class="text-caption font-weight-medium text-uppercase grey--text">{{ getFileExtension(resource.filename) }}</span>
              </div>

              <!-- Hover overlay with quick actions -->
              <v-fade-transition>
                <div v-if="resource.hover" class="resource-overlay d-flex align-center justify-center">
                  <v-btn
                    small
                    dark
                    fab
                    color="green"
                    class="mx-1 elevation-4"
                    @click="openWhatsAppDialog(resource)"
                  >
                    <v-icon size="18">mdi-whatsapp</v-icon>
                  </v-btn>
                  <v-btn
                    small
                    dark
                    fab
                    color="primary"
                    class="mx-1 elevation-4"
                    @click="openChatDialog(resource)"
                  >
                    <v-icon size="18">mdi-send</v-icon>
                  </v-btn>
                </div>
              </v-fade-transition>
            </div>

            <v-card-text class="pt-3 pb-1 px-4">
              <div class="d-flex align-start mb-1">
                <h3 class="text-body-2 font-weight-bold text--primary resource-title line-clamp-2">{{ resource.name }}</h3>
              </div>
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption grey--text">{{ resource.size }}</span>
                <v-chip x-small label :color="getTypeColor(resource.type)" class="font-weight-medium" text-color="white">
                  {{ resource.type || 'File' }}
                </v-chip>
              </div>
              <div class="text-caption grey--text text--lighten-1 mt-1">{{ resource.date }}</div>
            </v-card-text>

            <v-spacer />

            <v-card-actions class="px-4 py-3">
              <v-btn
                small
                text
                color="green darken-1"
                class="text-none font-weight-medium"
                @click="openWhatsAppDialog(resource)"
              >
                <v-icon left small>mdi-whatsapp</v-icon> WhatsApp
              </v-btn>
              <v-spacer />
              <v-btn
                small
                depressed
                color="primary"
                dark
                class="text-none font-weight-medium px-3"
                @click="openChatDialog(resource)"
              >
                <v-icon left small>mdi-message-text</v-icon> Chat
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- NO RESULTS -->
    <v-container v-else-if="selectedType && filteredResources.length === 0 && !loading" fluid class="pa-4">
      <v-row>
        <v-col cols="12" class="text-center py-12 empty-state">
          <v-icon size="64" color="grey lighten-2">mdi-folder-open-outline</v-icon>
          <p class="text-subtitle-1 mt-3 grey--text text--darken-1 font-weight-medium">No resources found</p>
          <p class="text-caption grey--text">Try adjusting your search or filter</p>
        </v-col>
      </v-row>
    </v-container>

    <!-- WHATSAPP DIALOG -->
    <v-dialog v-model="whatsappDialog" max-width="480px" persistent>
      <v-card class="dialog-card">
        <v-card-title class="dialog-header px-5 py-4">
          <v-avatar color="green lighten-5" size="40" class="me-3">
            <v-icon color="green">mdi-whatsapp</v-icon>
          </v-avatar>
          <span class="text-h6 font-weight-bold">Send via WhatsApp</span>
          <v-spacer />
          <v-btn icon small @click="whatsappDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4 px-5">
          <v-alert type="warning" outlined dense class="mb-4 rounded-lg">
            <div class="text-body-2">
              <strong>Note:</strong> Direct file sending requires the WhatsApp Business API. A download link will be sent instead.
            </div>
          </v-alert>

          <v-card outlined class="pa-3 mb-4 selected-resource-card">
            <div class="d-flex align-center">
              <v-avatar :color="getFileColor(selectedResource && selectedResource.filename)" size="40" class="me-3">
                <v-icon color="white" size="20">{{ getFileIcon(selectedResource && selectedResource.filename) }}</v-icon>
              </v-avatar>
              <div>
                <div class="text-body-2 font-weight-bold">{{ selectedResource && selectedResource.name }}</div>
                <div class="text-caption grey--text">{{ selectedResource && selectedResource.size }}</div>
              </div>
            </div>
          </v-card>

          <div class="text-subtitle-2 font-weight-bold mb-2">Message Preview</div>
          <v-card outlined class="pa-3 grey lighten-4 rounded-lg">
            <code class="whatsapp-preview text-body-2">{{ whatsappPreview }}</code>
          </v-card>
        </v-card-text>
        <v-card-actions class="px-5 py-4">
          <v-spacer />
          <v-btn text class="text-none font-weight-medium" @click="whatsappDialog = false">Cancel</v-btn>
          <v-btn
            color="green darken-1"
            dark
            depressed
            class="text-none font-weight-bold px-5"
            :disabled="!selectedResource"
            @click="sendWhatsAppMessage"
          >
            <v-icon left small>mdi-send</v-icon> Send Message
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CHAT DIALOG -->
    <v-dialog v-model="chatDialog" max-width="640px" persistent>
      <v-card class="dialog-card">
        <v-card-title class="dialog-header px-5 py-4">
          <v-avatar color="primary lighten-5" size="40" class="me-3">
            <v-icon color="primary">mdi-message-text</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">Send Resource</div>
            <div class="text-caption grey--text" v-if="selectedResource">{{ selectedResource.name }}</div>
          </div>
          <v-spacer />
          <v-btn icon small @click="closeChatDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="py-4 px-5">
          <v-alert v-if="!clients.length" type="info" dense text class="rounded-lg">Loading clients...</v-alert>

          <template v-else>
            <v-alert v-if="preparingFile" type="info" dense text class="mb-3 rounded-lg">
              <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
              Downloading file...
            </v-alert>

            <v-text-field
              v-model="clientSearch"
              label="Search clients"
              prepend-inner-icon="mdi-magnify"
              single-line
              hide-details
              dense
              outlined
              rounded
              class="mb-3"
            />

            <v-card outlined class="client-table-wrapper">
              <v-data-table
                v-model="selectedClients"
                :headers="clientHeaders"
                :items="filteredClientItems"
                :search="clientSearch"
                item-key="downline_id"
                show-select
                disable-pagination
                hide-default-footer
                dense
                height="280"
                fixed-header
                class="elevation-0"
              >
                <template #[`item.phone_number`]="{ item }">
                  <span class="grey--text">{{ item.phone_number || '-' }}</span>
                </template>
              </v-data-table>
            </v-card>

            <div class="d-flex align-center justify-space-between mt-3">
              <v-chip small color="primary lighten-5" text-color="primary" class="font-weight-medium">
                <v-icon left x-small>mdi-account-multiple</v-icon>
                {{ selectedClients.length }} selected
              </v-chip>
            </div>

            <v-alert
              v-if="sendStatus.message"
              :type="sendStatus.success ? 'success' : 'error'"
              dense
              text
              class="mt-3 rounded-lg"
            >
              {{ sendStatus.message }}
            </v-alert>
          </template>
        </v-card-text>

        <v-card-actions class="px-5 py-4">
          <v-btn text class="text-none font-weight-medium" @click="closeChatDialog">Cancel</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            dark
            depressed
            class="text-none font-weight-bold px-6"
            :disabled="!selectedClients.length || sending || preparingFile"
            :loading="sending"
            @click="sendToMultipleClients"
          >
            <v-icon left small>mdi-send</v-icon>
            Send to {{ selectedClients.length }} client{{ selectedClients.length > 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SUCCESS DIALOG -->
    <v-dialog v-model="successDialog" max-width="520px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-header px-5 py-4">
          <v-avatar color="success lighten-5" size="40" class="me-3">
            <v-icon color="success">mdi-check-circle</v-icon>
          </v-avatar>
          <span class="text-h6 font-weight-bold">Sent Successfully</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4 px-5">
          <p class="text-body-2 grey--text text--darken-1 mb-4">The resource has been sent to the following clients:</p>
          <v-list dense class="client-list rounded-lg">
            <v-list-item v-for="client in sentClients" :key="client.downline_id" class="px-2">
              <v-list-item-avatar size="32" color="primary lighten-5" class="mr-3">
                <v-icon size="16" color="primary">mdi-account</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ client.full_name }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">@{{ client.username || 'unknown' }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn x-small depressed color="primary" dark class="text-none" @click="openChatWith(client.downline_id)">
                  <v-icon left x-small>mdi-message-text</v-icon> Open
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="px-5 py-4">
          <v-spacer />
          <v-btn text class="text-none font-weight-medium" @click="successDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios'

const RESOURCE_TYPES = ['Document', 'Flyers', 'Video']

export default {
  name: 'MarketingResources',
  data() {
    return {
      resources: [],
      loading: false,
      search: '',
      selectedType: 'Flyers',

      whatsappDialog: false,
      selectedResource: null,

      chatDialog: false,
      clients: [],
      clientSearch: '',
      selectedClients: [],
      sending: false,
      sendStatus: { message: '', success: false },
      preparingFile: false,
      resourceBlob: null,
      resourceMimeType: '',
      resourceFilename: '',

      successDialog: false,
      sentClients: [],
    }
  },
  computed: {
    typeOptions() { return RESOURCE_TYPES.map(t => ({ text: t, value: t })) },
    filteredResources() {
      if (!this.search) return this.resources
      const q = this.search.toLowerCase()
      return this.resources.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      )
    },
    clientHeaders() {
      return [
        { text: 'Name', value: 'full_name' },
        { text: 'Phone', value: 'phone_number' },
      ]
    },
    filteredClientItems() {
      return this.clients.map(c => ({
        downline_id: c.downline_id,
        full_name: c.full_name,
        phone_number: c.phone_number,
        username: c.username,
      }))
    },
    whatsappPreview() {
      if (!this.selectedResource) return ''
      return `Hello, check this out:\n\n${this.selectedResource.name}\n${this.selectedResource.file_url}`
    },
  },
  async mounted() {
    this.fetchClients()
    this.fetchResources()
  },
  methods: {
    // --- FILE HELPERS ---
    getFileExtension(filename) {
      if (!filename) return 'FILE'
      const ext = filename.split('.').pop().toLowerCase()
      return ext || 'FILE'
    },
    getFileIcon(filename) {
      if (!filename) return 'mdi-file-document'
      const ext = filename.split('.').pop().toLowerCase()
      const map = {
        pdf: 'mdi-file-pdf',
        doc: 'mdi-file-word', docx: 'mdi-file-word',
        xls: 'mdi-file-excel', xlsx: 'mdi-file-excel',
        ppt: 'mdi-file-powerpoint', pptx: 'mdi-file-powerpoint',
        mp4: 'mdi-file-video', mov: 'mdi-file-video', avi: 'mdi-file-video',
        jpg: 'mdi-file-image', jpeg: 'mdi-file-image', png: 'mdi-file-image', gif: 'mdi-file-image',
        zip: 'mdi-folder-zip', rar: 'mdi-folder-zip',
        txt: 'mdi-file-document',
      }
      return map[ext] || 'mdi-file-document'
    },
    getFileColor(filename) {
      if (!filename) return 'grey'
      const ext = filename.split('.').pop().toLowerCase()
      const map = {
        pdf: 'red',
        doc: 'blue', docx: 'blue',
        xls: 'green', xlsx: 'green',
        mp4: 'purple', mov: 'purple',
        jpg: 'orange', jpeg: 'orange', png: 'orange', gif: 'orange',
      }
      return map[ext] || 'grey'
    },
    getTypeColor(type) {
      if (!type) return 'grey'
      const t = type.toLowerCase()
      if (t === 'image') return 'orange'
      if (t === 'video') return 'purple'
      if (t === 'document') return 'blue'
      if (t === 'flyers') return 'teal'
      return 'grey'
    },

    async fetchResources() {
      if (!this.selectedType) { this.resources = []; return }
      this.loading = true
      try {
        const url = `/api/v1/topup/get/ad_resources/${this.selectedType}/`
        const res = await axios.get(url)
        this.resources = this.formatResources(res.data)
      } catch (e) {
        console.error('Failed to load resources:', e)
        this.$store.commit('setSnackBarMessage', 'Failed to load marketing resources')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally { this.loading = false }
    },

    formatResources(data) {
      return data.map(r => ({
        id: r.id,
        name: r.resource_name,
        size: r.resource_size,
        type: r.resource_type,
        description: r.resource_description,
        file_url: r.get_file,
        filename: r.resource_filename || r.resource_name || 'file',
        date: r.get_date_str,
        hover: false,
      }))
    },

    async fetchClients() {
      try {
        const res = await axios.get('/api/v1/topup/my_downlines/')
        this.clients = (res.data.downlines || []).map(d => ({
          downline_id: d.downline_id,
          full_name: d.full_name,
          username: d.username,
          phone_number: d.phone_number || '',
        }))
      } catch (e) { console.error('Failed to load clients:', e) }
    },

    // ----- WHATSAPP -----
    openWhatsAppDialog(resource) { this.selectedResource = resource; this.whatsappDialog = true },
    sendWhatsAppMessage() {
      if (!this.selectedResource) return
      window.open(`https://wa.me/?text=${encodeURIComponent(this.whatsappPreview)}`, '_blank')
      this.whatsappDialog = false
    },

    // ----- CHAT -----
    async openChatDialog(resource) {
      this.selectedResource = resource
      this.selectedClients = []
      this.clientSearch = ''
      this.sendStatus = { message: '', success: false }
      this.resourceBlob = null
      this.resourceMimeType = ''
      this.resourceFilename = resource.filename || resource.name || 'file'
      this.preparingFile = true
      this.chatDialog = true

      try {
        const response = await axios.get(resource.file_url, { responseType: 'blob' })
        this.resourceBlob = response.data
        if (!response.data.type || response.data.type === 'application/octet-stream') {
          const ext = this.resourceFilename.split('.').pop().toLowerCase()
          const mimeMap = {
            jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
            pdf: 'application/pdf', mp4: 'video/mp4', doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          }
          this.resourceMimeType = mimeMap[ext] || 'application/octet-stream'
        } else {
          this.resourceMimeType = response.data.type
        }
      } catch (err) {
        console.error('Error downloading resource:', err)
        this.sendStatus = { message: 'Could not download the file.', success: false }
      } finally {
        this.preparingFile = false
      }
    },

    closeChatDialog() {
      this.chatDialog = false
      this.selectedResource = null
      this.selectedClients = []
      this.resourceBlob = null
    },

    async sendToMultipleClients() {
      if (!this.selectedResource || !this.selectedClients.length || !this.resourceBlob) return
      this.sending = true
      this.sendStatus = { message: '', success: false }

      let messageType = 'file'
      if (this.resourceMimeType.startsWith('image/')) messageType = 'image'
      else if (this.resourceMimeType.startsWith('video/')) messageType = 'video'

      const successful = []
      const failed = []

      for (const client of this.selectedClients) {
        try {
          const fd = new FormData()
          fd.append('downline_id', client.downline_id)
          fd.append('message', this.selectedResource.name)
          fd.append('message_type', messageType)
          fd.append('file', this.resourceBlob, this.resourceFilename)
          await axios.post('/api/v1/topup/send_to_downline/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          successful.push(client)
        } catch (err) {
          failed.push(client)
          console.error(`Failed to send to ${client.full_name}:`, err)
        }
      }

      this.sending = false
      if (failed.length === 0) {
        this.sendStatus.message = `File sent successfully to ${successful.length} client(s).`
        this.sendStatus.success = true
        this.sentClients = successful
        this.chatDialog = false
        this.successDialog = true
      } else {
        this.sendStatus.message = `Sent to ${successful.length} client(s), but failed for ${failed.length}.`
        this.sendStatus.success = false
      }
    },

    openChatWith(downlineId) {
      this.successDialog = false
      this.$router.push({ name: 'topup-chat', query: { client: downlineId } })
    },
  },
}
</script>

<style scoped>
.marketing-resources {
  border-radius: 16px;
  overflow: hidden;
}

.resources-header {
  background: linear-gradient(to right, #ffffff, #fafafa);
}

.resource-filter {
  max-width: 160px;
}

.resource-search {
  max-width: 220px;
}

.resource-card {
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0,0,0,0.06);
}

.resource-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0,0,0,0.15) !important;
  border-color: rgba(0,0,0,0.12);
}

.resource-preview-wrapper {
  height: 150px;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.resource-image {
  transition: transform 0.5s ease;
}

.resource-card:hover .resource-image {
  transform: scale(1.05);
}

.file-icon-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.resource-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
}

.resource-title {
  min-height: 2.4em;
  line-height: 1.4;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  opacity: 0.8;
}

.dialog-card {
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  background: #ffffff;
}

.whatsapp-preview {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  color: #555;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.85rem;
}

.selected-resource-card {
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid rgba(0,0,0,0.08);
}

.client-table-wrapper {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.08);
}

.client-list {
  background: #fafafa;
  border: 1px solid rgba(0,0,0,0.06);
}

@media (max-width: 600px) {
  .resource-preview-wrapper {
    height: 120px;
  }
  .resource-filter, .resource-search {
    max-width: 100%;
  }
  .resources-header {
    padding: 12px 16px !important;
  }
}
</style>
